import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { UBER_DELIVERY_DATE } from '~/constants/organization_notification';
import {
  GetUnreadUberDeliveryNotificationsDocument,
  MarkOrganizationNotificationDocument,
  OrganizationNotificationFragment,
} from '~/graphql/generated/graphql';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { uberDeliveryNotificationsState } from '~/state/layouts/SharedAppShell/atoms';

export const useSubscriptionUberDeliveryNotifications = () => {
  const [{ notifications, loaded }, setState] = useAtom(uberDeliveryNotificationsState);
  const [event, clear] = useSubscriptions('OrganizationNotification', ['created', 'updated']);
  const [markNotification] = useMutation(MarkOrganizationNotificationDocument);
  const [getNotifications, { data, loading }] = useLazyQuery(
    GetUnreadUberDeliveryNotificationsDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (loading) return;
    if (!data?.me || data.me.__typename !== 'Practitioner') return;

    const organizationNotifications = data.me.organization.unreadOrganizationNotifications.nodes;
    const notifications = organizationNotifications.filter(
      (n): n is OrganizationNotificationFragment => !!n.appointment?.uberDelivery,
    );

    const needPlaySoundNotificationCount = notifications.filter(
      (n) => n.type === 'time_elapsed_uber_delivery' || n.type === 'unfulfilled_uber_delivery',
    ).length;

    setState((_state) => ({
      ..._state,
      notifications,
      needPlaySoundNotificationCount,
      loaded: true,
    }));
  }, [data, loading, setState]);

  const handleClose = useCallback(
    async (notificationId: string) => {
      const newNotifications = notifications.filter(
        (notification) => notification.id !== notificationId,
      );
      setState((_state) => ({ ..._state, notifications: newNotifications }));

      const notification = notifications.find((n) => n.id === notificationId);

      if (notification) {
        await markNotification({
          variables: {
            input: {
              organizationNotificationId: notification.id,
            },
          },
        });
      }
    },
    [markNotification, notifications, setState],
  );

  useEffect(() => {
    if (event) {
      getNotifications({ variables: { from: UBER_DELIVERY_DATE } });
      clear();
    }
  }, [clear, event, getNotifications]);

  useEffect(() => {
    // ログイン時に通知を取得する
    if (!loaded) {
      getNotifications({ variables: { from: UBER_DELIVERY_DATE } });
    }
  }, [getNotifications, loaded]);

  return {
    handleClose,
  };
};
